<template>
	<div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('assetTypeDetail') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId ">
                        {{ data.name }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    {{ data.name }} detail
                </li>
            </ol>
        </nav>
        <div class="row">
            <div class="mb-3 col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
            <div class="mb-3 col-md-8">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description">
            </div>
        </div>
        <div class="row">
            <div class="mb-3 col-md-4">
                <label class="form-label" for="assetAttribute" required>{{ $t('assetAttribute') }}</label>
                <select id="assetAttribute" class="form-select" v-model="data.assetAttribute">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                    <template v-if="missingDatasetHeaders.length > 0" >
                        <option v-for="(missingDatasetHeader, i) in missingDatasetHeaders" :key="i" disabled  v-bind:value="missingDatasetHeader.name">{{ missingDatasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
            <div class="mb-3 col-md-4">
                <label class="form-label" for="assetAttributeYear" required>{{ $t('assetAttributeYear') }}</label>
                <select id="assetAttributeYear" class="form-select" v-model="data.assetAttributeYear">
                    <option selected value=""></option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                    <template v-if="missingDatasetHeaders.length > 0" >
                        <option v-for="(missingDatasetHeader, i) in missingDatasetHeaders" :key="i" disabled  v-bind:value="missingDatasetHeader.name">{{ missingDatasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
            <div class="mb-3 col-md-4">
                <label class="form-label" for="lifeCycleAgeAttribute" required>{{ $t('lifeCycleAgeAttribute') }}</label>
                <select id="lifeCycleAgeAttribute" class="form-select" v-model="data.lifeCycleAgeAttribute">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                    <template v-if="missingDatasetHeaders.length > 0" >
                        <option v-for="(missingDatasetHeader, i) in missingDatasetHeaders" :key="i" disabled  v-bind:value="missingDatasetHeader.name">{{ missingDatasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
            <div class="mb-3 col-md-4">
                <label class="form-label" for="weightAttribute" required>{{ $t('weightAttribute') }}</label>
                <select id="weightAttribute" class="form-select" v-model="data.weightAttribute">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                    <template v-if="missingDatasetHeaders.length > 0" >
                        <option v-for="(missingDatasetHeader, i) in missingDatasetHeaders" :key="i" disabled  v-bind:value="missingDatasetHeader.name">{{ missingDatasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
            <div class="mb-3 col-md-4">
                <label class="form-label" for="visualizationValue" required>{{ $t('visualizationValue') }}</label>
                <select id="visualizationValue" class="form-select" v-model="data.visualizationValue">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="visualizationValues.length > 0" >
                        <option v-for="(visualizationValue, i) in visualizationValues" :key="i" v-bind:value="visualizationValue.id">{{ visualizationValue.name }}</option>
                    </template>
                </select>
            </div>
            <div class="mb-3 col-md-4">
                <label class="form-label">&nbsp;</label>

                <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="data.recalculateLifeCycleAge"  id="recalculateLifeCycleAge" checked>
                    <label class="form-check-label" for="recalculateLifeCycleAge">
                        {{ $t('recalculateLifeCycleAge') }}
                    </label>
                </div>
            </div>
        </div>
        <div class="row mb-4">
            <div class="mb-4" >
                <h4>{{ $t('classificationOfAssetAttribute') }}</h4>
                <table class="table">
                    <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">{{ $t('startAssetPerformance') }}</th>
                            <th scope="col">{{ $t('endAssetPerformance') }}</th>
                            <th scope="col">{{ $t('name') }}</th>
                            <th scope="col">{{ $t('color') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(classificationOfAssetPerformance, i) in data.assetTypeClassification" class="align-middle"  :key="i">
                            <th scope="row"><i class="fa fa-remove" @click="removeClassification(i)"></i></th>
                            <td>
                                <input v-model="classificationOfAssetPerformance.startAssetPerformance" :disabled="i != 0"  type="number" class="form-control" :id="'startAssetPerformance_' + i">
                            </td>
                            <td>
                                <input v-model="classificationOfAssetPerformance.endAssetPerformance" @input="updateAssetTypeClassificationStart(classificationOfAssetPerformance.endAssetPerformance, i)" type="number" class="form-control" :id="'endAssetPerformance_' + i">
                            </td>
                            <td>
                                <input v-model="classificationOfAssetPerformance.name" type="text" class="form-control" :id="'name_' + i">
                            </td>
                            <td>
                                <input v-model="classificationOfAssetPerformance.color" type="color" :id="'color_' + i">
                            </td>
                        </tr>
                    </tbody>
                </table>
                <button class="btn btn-secondary w-25 mx-auto d-block" @click="addClassification"><i class="fa-solid fa-plus"></i></button>
            </div>
        </div>
        <div class="row mb-4">
            <h4>{{ $t('infosetAttributes') }}</h4>
            <table class="table ">
                <thead>
                    <tr>
                        <th scope="col">{{ $t('leading') }}</th>
                        <th scope="col">{{ $t('headerName') }}</th>
                        <th scope="col">{{ $t('alias') }}</th>
                        <th scope="col"></th>
                        <th scope="col"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(infoSet, i) in orderedClientHeaders(clientHeaders)" class="align-middle" :key="i">
                        <td>
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="infoSet.leading" :id="'leading_' + i">
                            </div>
                        </td>
                        <td>
                            <input v-model="infoSet.headerName" disabled type="text" class="form-control" :id="'headerName_' + i">
                        </td>
                        <td>
                            <input v-model="infoSet.alias" :disabled="!infoSet.leading" type="text" class="form-control" :id="'alias_' + i">
                        </td>
                        <td v-if="infoSet.leading"><i v-if='i < clientHeaders.length-1' class="fa fa-arrow-down" @click="moveAssetTypeInfoSetDown(i)"/></td>
                        <td v-if="infoSet.leading"><i v-if='i != 0' class="fa fa-arrow-up" @click="moveAssetTypeInfoSetUp(i)" /></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="d-grid gap-2 d-md-flex ">
            <template v-if="isUserAdmin">
                <button type="button" class="btn btn-primary" @click="onSubmit" >{{ $t('save') }}</button>
                <button type="button" class="btn btn-danger" @click="showDeleteAssetType = true">{{ $t('delete') }}</button>
            </template>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })">{{ $t('back') }}</button>
        </div>
        <AssetTypeDeleteModal v-if="showDeleteAssetType" @reload="$router.push({ path: `/modeling/${this.$route.params.id}` })" @closeDeleteDialog="showDeleteAssetType = false"/>
    </div>
</template>

<script>
import AssetTypeApi from "../../api/server/assetType";
import ModelingApi from "../../api/server/modeling";
import PerformanceApi from "../../api/server/performance";
import DatasetApi from "../../api/server/dataset";
import CheckIfUserIsAdmin from '../../authCache';
import AssetTypeDeleteModal from "./AssetTypeDeleteModal.vue";
import ParameterApi from "../../api/server/parameter";
import KpiApi from "../../api/server/kpi";

export default {
    name: "assettypecreate",
    watch: {
        $route: "requestData",
    },
    components: {
        AssetTypeDeleteModal
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.pending = true;
            this.isUserAdmin = await CheckIfUserIsAdmin();

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            var assetTypeId = this.$route.params.assetTypeId;
            this.data = await AssetTypeApi.assetTypeDetail(this.$route.params.id, assetTypeId);

               
            var modelingInfo = await ModelingApi.modelingInfo(this.$route.params.id);
            var assetType = modelingInfo.assetTypes.find(a => a.id == this.$route.params.assetTypeId);
            
            var performances = await PerformanceApi.performanceList(this.$route.params.id);
            var performancesForCurrentAssetType = performances.filter(p => p.assetTypeId == this.$route.params.assetTypeId);
            console.log(assetType)
            if(
                assetType.activities.length > 0 ||
                assetType.benefits.length > 0 ||
                assetType.lifeCycles.length > 0 ||
                assetType?.parameters?.length > 0 ||
                assetType?.keyPerformanceIndicators?.length > 0 ||
                performancesForCurrentAssetType.length > 0) {
                this.assetTypeNotEmpty = true;
            }

            for(var i in modelingInfo.forecastModels) {
                var forecast = modelingInfo.forecastModels[i];
                var exists = forecast.assetTypes.find(assetType => assetType.assetTypeId == this.$route.params.assetTypeId);
                if(exists) {
                    this.foundAssetTypes.push({ 
                        forecastId: forecast.id,
                        name: "Forecast - " + forecast.name
                    });
                }
            }

            var visualizationValues = [{ name: "Performance", id: "performance" }];

            var parameterList = await ParameterApi.parameterList(this.$route.params.id);
            var keyPerformanceIndicators = await KpiApi.kpiList(this.$route.params.id);
            for(var i in parameterList.filter(p => p.assetTypeId == this.$route.params.assetTypeId)) {
                var parameter = parameterList[i];
                visualizationValues.push({ name: parameter.name, id: "parameter_" + parameter.id });
            }
            for(var i in keyPerformanceIndicators.filter(k => k.assetTypeId == this.$route.params.assetTypeId)) {
                var kpi = keyPerformanceIndicators[i];
                visualizationValues.push({ name: kpi.name, id: "kpi_" + kpi.id });
            }
            this.visualizationValues = visualizationValues;

            var dataset = await DatasetApi.datasetDetail(this.$route.params.id, this.data.datasetId);
            var datasetContent = JSON.parse(dataset.content);
            this.datasetHeaders = datasetContent["headers"];

            var clientHeaders = [];

            for(var i in this.data.assetTypeInfoSet) {
                var foundInfoset = this.data.assetTypeInfoSet[i];
                clientHeaders.push({ id: foundInfoset.id, leading: foundInfoset.leading, headerName: foundInfoset.headerName, alias: foundInfoset.alias, order: foundInfoset.order });
            }

            for(var i in this.datasetHeaders) {
                var header = this.datasetHeaders[i];
                var foundInfoset = clientHeaders.findIndex(a => a.headerName == header.name);
                if(foundInfoset == -1) {
                    clientHeaders.push({ leading: false, headerName: header.name, alias: header.alias, order: this.getLastOrder(clientHeaders)});
                }
            }
            this.clientHeaders = clientHeaders;

            this.assetTypeClassification = this.data.assetTypeClassification;

            var assetAttribute = this.data.assetAttribute;
            if(this.datasetHeaders.findIndex(d => d.name == assetAttribute) < 0 && this.missingDatasetHeaders.findIndex(d => d.name == assetAttribute) < 0) {
                this.missingDatasetHeaders.push({ name: assetAttribute, alias: assetAttribute });
            }

            var lifeCycleAgeAttribute = this.data.lifeCycleAgeAttribute;
            if(this.datasetHeaders.findIndex(d => d.name == lifeCycleAgeAttribute) < 0 && this.missingDatasetHeaders.findIndex(d => d.name == lifeCycleAgeAttribute) < 0) {
                this.missingDatasetHeaders.push({ name: lifeCycleAgeAttribute, alias: lifeCycleAgeAttribute });
            }

            var weightAttribute = this.data.weightAttribute;
            if(this.datasetHeaders.findIndex(d => d.name == weightAttribute) < 0 && this.missingDatasetHeaders.findIndex(d => d.name == weightAttribute) < 0) {
                this.missingDatasetHeaders.push({ name: weightAttribute, alias: weightAttribute });
            }
            
            var assetAttributeYear = this.data.assetAttributeYear;
            if(this.datasetHeaders.findIndex(d => d.name == assetAttributeYear) < 0 && this.missingDatasetHeaders.findIndex(d => d.name == assetAttributeYear) < 0) {
                this.missingDatasetHeaders.push({ name: assetAttributeYear, alias: assetAttributeYear });
            } 

            this.pending = false;
        },
        removeClassification(rowIndex) {
            this.assetTypeClassification.splice(rowIndex, 1);
        },
        addClassification() {
            this.assetTypeClassification.push({  
                startAssetPerformance: this.getClassification(),
                endAssetPerformance: 0,
                name: "",
                color: "",
            })
        },
        getClassification() {
            var minAssetPerformanceFrom = 0;
            if(this.assetTypeClassification.length > 0)
                minAssetPerformanceFrom = this.assetTypeClassification[this.assetTypeClassification.length -1].endAssetPerformance;

            return minAssetPerformanceFrom;
        },
        updateAssetTypeClassificationStart(value, i) {
            var range = this.assetTypeClassification[i+1];
            if(range) {
                range.startAssetPerformance = value;
            }
        },
        isFormValid() {
            var assetAttribute = this.data.assetAttribute;
            if(this.datasetHeaders.findIndex(d => d.name == assetAttribute) < 0 && assetAttribute != "")
                return false;

            var lifeCycleAgeAttribute = this.data.lifeCycleAgeAttribute;
            if(this.datasetHeaders.findIndex(d => d.name == lifeCycleAgeAttribute) < 0 && lifeCycleAgeAttribute != "")
                return false;

            var weightAttribute = this.data.weightAttribute;
            if(this.datasetHeaders.findIndex(d => d.name == weightAttribute) < 0 && weightAttribute != "")
                return false;

            return true;
        },
        async onSubmit() {
            if(this.data.name.length > 0) {
                var assetTypeInfoSet = [];
                for(var i in this.clientHeaders) {
                    var header = this.clientHeaders[i];
                    if(header.leading)
                        assetTypeInfoSet.push({ id: header.id, leading: header.leading, headerName: header.headerName, alias: header.alias, order: header.order });
                }
                this.data.assetTypeInfoSet = assetTypeInfoSet;

                this.data.assetTypeClassification = this.assetTypeClassification;

                await AssetTypeApi.assetTypeUpdate(this.$route.params.id, this.data);
                if(!this.isFormValid())
                    this.showWarning();
                else 
                    this.showSuccess();
            } else {
                this.showEmptyNameError();
            }
        }, 
        moveAssetTypeInfoSetUp(i) {
            var assetTypeInfoSet = this.clientHeaders;
            if (i > 0) {
                let indexOfInfoSetUp = i - 1;
                let tmpOrder = assetTypeInfoSet[indexOfInfoSetUp].order;
                assetTypeInfoSet[indexOfInfoSetUp].order = assetTypeInfoSet[i].order;
                assetTypeInfoSet[i].order = tmpOrder;

                assetTypeInfoSet.sort((a, b) => a.order - b.order);

                this.clientHeaders = assetTypeInfoSet;
            }
        },
        moveAssetTypeInfoSetDown(i) {
            var assetTypeInfoSet = this.clientHeaders;
            if (i >= 0) {
                let indexOfInfoSetDown = i + 1;
                let tmpOrder = assetTypeInfoSet[indexOfInfoSetDown].order;
                assetTypeInfoSet[indexOfInfoSetDown].order = assetTypeInfoSet[i].order;
                assetTypeInfoSet[i].order = tmpOrder;

                assetTypeInfoSet.sort((a, b) => a.order - b.order);

                this.clientHeaders = assetTypeInfoSet;
            }
        },
        getLastOrder(clientHeaders) {
            var order = 0;
            if(clientHeaders[clientHeaders.length-1])
                order = clientHeaders[clientHeaders.length-1].order + 1;

            return order;
        },
        orderedClientHeaders(clientHeaders) {
            return clientHeaders.sort((a, b) => a.order - b.order);
        }
    },
    data() {
        return {
            data: {
                name: "",
                id: 0
            },
            modelingName: null,
            pending: true,
            datasetHeaders: [],
            missingDatasetHeaders: [],
            assetTypeClassification: [],
            clientHeaders: [],
            isUserAdmin: false,
            foundAssetTypes: [],
            visualizationValues: [],
            assetTypeNotEmpty: false,
            showDeleteAssetType: false
        }
    },
    notifications: {
        showWarning: { 
            title: 'warning',
            message: 'warningAttributeRemoved',
            type: 'warn'
        },
        showSuccess: { 
            title: 'success',
            message: 'assetTypeInfosetUpdated',
            type: 'success'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    }
};
</script>